import type { Part } from '../../domain/inventory';
import { humanizeIdentifier } from '../../lib/formatting';

/**
 * A serialized line is stored as a list of acceptable part IDs, but people
 * think "a Gen 2 winch", not "these seven builds". The editor shows a part
 * type plus, optionally, the generation or builds to restrict it to; every
 * build of the type is the default.
 */
export interface PartChoice {
  /** The one part type the line's parts belong to; '' when none chosen. */
  type: string;
  /** Builds the line is restricted to; empty means any build of the type. */
  partIds: string[];
  /** The parts span several types (older lines and templates). */
  mixed: boolean;
}

export function partChoiceOf(partIds: readonly string[], parts: readonly Part[]): PartChoice {
  const byId = new Map(parts.map((part) => [part.id, part]));
  const types = new Set(partIds.map((id) => byId.get(id)?.type ?? `unknown:${id}`));
  if (types.size === 0) return { type: '', partIds: [], mixed: false };
  const [type] = types;
  if (types.size > 1 || type === undefined || type.startsWith('unknown:')) {
    return { type: '', partIds: [...partIds], mixed: true };
  }
  const allOfType = partIdsOfType(parts, type);
  const any = allOfType.length > 0 && allOfType.every((id) => partIds.includes(id));
  return { type, partIds: any ? [] : [...partIds], mixed: false };
}

/** The part IDs a line accepts for a type and an optional build restriction. */
export function partIdsForChoice(
  parts: readonly Part[],
  type: string,
  restrictTo: readonly string[],
): string[] {
  if (!type) return [];
  const ofType = partIdsOfType(parts, type);
  const chosen = ofType.filter((id) => restrictTo.includes(id));
  return chosen.length > 0 ? chosen : ofType;
}

/** Newest build first: part IDs grow with each build of a type. */
export function partIdsOfType(parts: readonly Part[], type: string): string[] {
  return parts
    .filter((part) => part.type === type)
    .map((part) => part.id)
    .sort((left, right) => right.localeCompare(left, undefined, { numeric: true }));
}

export type PartGeneration = 'gen1' | 'gen2';

export const partGenerationLabels: Record<PartGeneration, string> = {
  gen1: 'Gen 1',
  gen2: 'Gen 2',
};

/**
 * Gen 2 is the VVDN build (VV serials: camera, winch, powerbox); every
 * earlier build is Gen 1 (G8 serials: camera, winch, unit controller), as
 * Field Services count them. Order makers think in generations, not builds.
 */
export function partGeneration(part: Pick<Part, 'build'>): PartGeneration {
  return part.build === 'VVDN' ? 'gen2' : 'gen1';
}

/** The type's part IDs of one generation, newest build first. */
export function partIdsOfGeneration(
  parts: readonly Part[],
  type: string,
  generation: PartGeneration,
): string[] {
  return partIdsOfType(parts, type).filter((id) => {
    const part = parts.find((candidate) => candidate.id === id);
    return part !== undefined && partGeneration(part) === generation;
  });
}

/** The generations a type has builds of, Gen 1 first. */
export function generationsOfType(parts: readonly Part[], type: string): PartGeneration[] {
  return (['gen1', 'gen2'] as const).filter(
    (generation) => partIdsOfGeneration(parts, type, generation).length > 0,
  );
}

/**
 * The generation a choice is restricted to, when its builds are exactly one
 * generation's of a type that has both; undefined for any build or a hand-picked set.
 */
export function generationOfChoice(
  parts: readonly Part[],
  choice: PartChoice,
): PartGeneration | undefined {
  if (choice.mixed || choice.partIds.length === 0) return undefined;
  if (generationsOfType(parts, choice.type).length < 2) return undefined;
  return generationsOfType(parts, choice.type).find((generation) => {
    const ids = partIdsOfGeneration(parts, choice.type, generation);
    return ids.length === choice.partIds.length && ids.every((id) => choice.partIds.includes(id));
  });
}

/** A line's acceptable parts in words: "Winch system, any build". */
export function describePartChoice(
  partIds: readonly string[],
  parts: readonly Part[],
  partName: (id: string) => string,
): string {
  const choice = partChoiceOf(partIds, parts);
  if (choice.mixed || !choice.type) return partIds.map(partName).join(' or ');
  const type = humanizeIdentifier(choice.type);
  if (choice.partIds.length === 0) return `${type}, any build`;
  const generation = generationOfChoice(parts, choice);
  if (generation) return `${type}, ${partGenerationLabels[generation]}`;
  const builds = choice.partIds.map((id) => parts.find((part) => part.id === id)?.build ?? id);
  return `${type}: ${builds.join(' or ')}`;
}
